import { ColumnsType } from 'antd/es/table';
import { RecitationStatusItem } from '../../../../constants/types/lms/recitation/type';
import TextWinds from '../../../common/TextWinds';
import { formatToKstDate } from '../../../../api/utils/util-func';
import {
  CheckCircleIcon,
  EllipsisHorizontalCircleIcon,
  MagnifyingGlassCircleIcon,
  MinusCircleIcon,
  StopCircleIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from 'antd';

const getRecitationStatusMeta = (recitationStatus: RecitationStatusItem) => {
  if (recitationStatus.status === 'PASSED') {
    return {
      label: '합격',
      className: 'bg-green-50 text-green-700',
      icon: CheckCircleIcon,
    };
  }

  if (recitationStatus.status === 'FAILED') {
    return {
      label: '불합격',
      className: 'bg-red-50 text-red-600',
      icon: XCircleIcon,
    };
  }

  if (recitationStatus.status === 'ANALYZING') {
    return {
      label: 'AI분석 중',
      className: 'bg-amber-100 text-amber-800',
      icon: MagnifyingGlassCircleIcon,
    };
  }

  if (recitationStatus.status === 'IN_PROGRESS') {
    return {
      label: '진행 중',
      className: 'bg-blue-50 text-blue-600',
      icon: EllipsisHorizontalCircleIcon,
    };
  }

  if (recitationStatus.status === 'NOT_STARTED') {
    return {
      label: '진행 전',
      className: 'bg-gray1 text-gray7',
      icon: StopCircleIcon,
    };
  }

  return {
    label: '비정상 종료',
    className: 'bg-gray1 text-gray7',
    icon: MinusCircleIcon,
  };
};

export const columns = ({
  recitationStatusList,
  handleResultClick,
  handleDelete,
}: {
  recitationStatusList: RecitationStatusItem[];
  handleResultClick: (assignment: RecitationStatusItem) => void;
  handleDelete: (assignment: RecitationStatusItem) => void;
}): ColumnsType<RecitationStatusItem> => [
  {
    title: 'No.',
    key: 'no',
    width: 70,
    align: 'center',
    render: (_, __, index) => (
      <TextWinds type="content_body3" color="gray7" className="font-bold">
        {recitationStatusList.length - index}
      </TextWinds>
    ),
  },
  {
    title: '학생',
    key: 'student',
    width: 100,
    align: 'center',
    render: (_, recitationStatus) => (
      <div className="min-w-0">
        <TextWinds
          type="content_body2"
          color="gray9"
          className="block truncate font-bold"
        >
          {recitationStatus.studentName ?? '-'}
        </TextWinds>

        <TextWinds type="caption1" color="gray6" className="block truncate">
          {recitationStatus.studentUid ?? '-'}
        </TextWinds>
      </div>
    ),
  },
  {
    title: 'Level',
    dataIndex: 'level',
    key: 'level',
    width: 120,
    align: 'center',
    render: (level: string) => (
      <TextWinds type="content_body3" color="gray8" className="font-medium">
        {level ? `Level ${level}` : '-'}
      </TextWinds>
    ),
  },
  {
    title: 'Book',
    dataIndex: 'bookTitle',
    key: 'bookTitle',
    width: 120,
    align: 'center',
    render: (bookTitle: string) => (
      <TextWinds type="content_body3" color="gray8" className="font-medium">
        {bookTitle}
      </TextWinds>
    ),
  },
  {
    title: '마감 기한',
    key: 'dueDate',
    width: 180,
    align: 'center',
    render: (_, assignment) => (
      <TextWinds type="content_body3" color="gray8" className="font-medium">
        {assignment.dueDate
          ? formatToKstDate(assignment.dueDate, 'YYYY.MM.DD HH:mm')
          : '-'}
      </TextWinds>
    ),
  },
  {
    title: '응시 일시',
    key: 'testDate',
    width: 180,
    align: 'center',
    render: (_, assignment) => (
      <TextWinds type="content_body3" color="gray8" className="font-medium">
        {assignment.attemptedAt
          ? formatToKstDate(assignment.attemptedAt, 'YYYY.MM.DD HH:mm')
          : '미응시'}
      </TextWinds>
    ),
  },
  {
    title: '결과',
    key: 'result',
    width: 140,
    align: 'center',
    render: (_, assignment) => {
      const statusMeta = getRecitationStatusMeta(assignment);
      const StatusIcon = statusMeta.icon;

      return (
        <span
          className={[
            'inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold',
            statusMeta.className,
          ].join(' ')}
        >
          <StatusIcon className="h-4 w-4" />
          {statusMeta.label}
        </span>
      );
    },
  },
  {
    title: '점수',
    dataIndex: 'score',
    key: 'score',
    width: 90,
    align: 'center',
    render: (score: number | null) => (
      <TextWinds
        type="content_body2"
        className={[
          'font-bold',
          score == null
            ? 'text-gray7'
            : score >= 80
              ? 'text-green-700'
              : score >= 60
                ? 'text-blue-600'
                : 'text-red-500',
        ].join(' ')}
      >
        {score == null ? '—' : `${score}점`}
      </TextWinds>
    ),
  },
  {
    title: '응시결과',
    key: 'result',
    width: 110,
    align: 'center',
    render: (_, assignment) => {
      const canViewResult =
        assignment.status === 'PASSED' || assignment.status === 'FAILED';

      return (
        <Button
          type="default"
          size="small"
          disabled={!canViewResult}
          className={[
            'h-8 rounded-lg px-3 text-xs font-bold',
            canViewResult
              ? '!border-purple5 !bg-white !text-purple5 hover:!border-purple6 hover:!bg-purple1 hover:!text-purple6'
              : '!border-gray3 !bg-gray1 !text-gray5',
          ].join(' ')}
          onClick={() => {
            handleResultClick(assignment);
          }}
        >
          결과보기
        </Button>
      );
    },
  },
  {
    title: '삭제',
    key: 'delete',
    width: 110,
    align: 'center',
    render: (_, assignment) => {
      const canDelete = assignment.status === 'NOT_STARTED';
      return (
        <Button
          type="default"
          size="small"
          disabled={!canDelete}
          className={[
            'h-8 rounded-lg px-3 text-xs font-bold',
            canDelete
              ? '!border-red-200 !bg-white !text-red-500 hover:!border-red-400 hover:!bg-red-50 hover:!text-red-500'
              : '!cursor-not-allowed !border-gray3 !bg-gray1 !text-gray5',
          ].join(' ')}
          onClick={() => {
            if (!canDelete) return;

            handleDelete(assignment);
          }}
        >
          <TrashIcon className="h-4 w-4" />
          삭제
        </Button>
      );
    },
  },
];
